import { Box, Theme, Typography } from "@outschool/backpack";
import {
  CategoryPage,
  ContentfulMagnetPage,
} from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { magnetPagePath } from "@outschool/routes";
import { DEFAULT_PROFILE_IMAGE } from "@outschool/ui-components-shared";
import {
  Carousel,
  Container,
  Avatar,
} from "@outschool/ui-legacy-component-library";
import { pxToRem, useIsMounted, useLinkComponent } from "@outschool/ui-utils";
import { CSSProperties } from "@styled-system/css";
import React from "react";

import EnglishOnly from "../EnglishOnly";

export default function SimilarArticlesMagnetPageCarousel({
  articles,
  category,
  title,
  leftAlign = false,
  carouselSx = {},
  basePath = "/articles",
}: {
  articles: ContentfulMagnetPage[];
  category?: CategoryPage;
  title?: JSX.Element | string;
  leftAlign?: boolean;
  carouselSx?: CSSProperties;
  basePath?: string;
}) {
  const isMounted = useIsMounted();
  const { t } = useTranslation(
    "ui-components-website\\SimilarArticlesMagnetPageCarousel"
  );

  if (articles.length === 0) {
    return (
      <Box
        sx={{
          marginTop: "40px",
        }}
      />
    );
  }

  const categoryName = category ? category?.nameTitleCased : null;

  return (
    <EnglishOnly>
      <Container sx={{ marginTop: "40px", marginBottom: "50px" }}>
        <Typography
          sx={(theme: Theme) => ({
            margin: "auto",
            fontWeight: 500,
            fontSize: `${theme.typography.pxToRem(28)} !important`,
          })}
          emphasized
          variant="h3"
        >
          {title ? (
            title
          ) : category ? (
            <>{t("Similar {{ categoryName }} articles", { categoryName })}</>
          ) : (
            <>{t("Articles you may find helpful")}</>
          )}
        </Typography>
        {isMounted && (
          <Carousel
            sx={{
              maxWidth: 1024,
              marginTop: "32px",
              ...carouselSx,
            }}
            leftAlign={leftAlign}
          >
            {articles.map(el => (
              <CarouselCard
                key={el.title}
                magnetPageSlug={el.magnetPageSlug ?? undefined}
                title={el.title ?? undefined}
                authorName={el.author?.name ?? undefined}
                authorImage={el.author?.profileImage?.url ?? undefined}
                headerImageURL={el.headerImage?.url ?? undefined}
                basePath={el.path ? "/" + el.path : basePath}
                gradeNameSlug={el.gradeNameSlug ?? undefined}
              />
            ))}
          </Carousel>
        )}
      </Container>
    </EnglishOnly>
  );
}

const CarouselCard = ({
  magnetPageSlug,
  title,
  authorName,
  authorImage,
  headerImageURL,
  basePath,
  gradeNameSlug,
}: {
  magnetPageSlug?: string;
  title?: string;
  authorName?: string;
  authorImage?: string;
  headerImageURL?: string;
  basePath?: string;
  gradeNameSlug?: string;
}) => {
  const Link = useLinkComponent();

  if (!magnetPageSlug || !title) {
    return null;
  }

  const LinkContents = () => (
    <Box
      sx={{
        width: "275px",
        marginRight: "16px",
        textAlign: "left",
      }}
    >
      <Box
        sx={{
          borderRadius: 10,
          height: "200px",
          width: "100%",
          margin: "auto",
          backgroundPosition: "center",
          backgroundImage: `url(${headerImageURL})`,
          backgroundSize: "cover",
        }}
      />

      <Box
        flex
        sx={{
          flexDirection: "column",
          paddingTop: "16px",
          paddingBottom: "10px",
          gap: "6px",
          color: "common.black",
        }}
      >
        <Box
          flex
          sx={{
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Avatar
            src={authorImage || DEFAULT_PROFILE_IMAGE}
            alt={authorName}
            size={24}
          />
          <Typography
            variant="inherit"
            sx={{
              fontSize: pxToRem(14),
            }}
          >
            {authorName}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "275px",
          }}
        >
          <Typography
            variant="inherit"
            sx={{
              fontSize: pxToRem(18),
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Link
      trackingName="magnet_page_similar_article_link"
      trackingUniqueId={magnetPageSlug}
      to={magnetPagePath(magnetPageSlug, basePath, gradeNameSlug)}
    >
      <LinkContents />
    </Link>
  );
};
